import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { cellSize, parseAddress } from "@/lib/api/address";
import { type HeatScale, type MapCell } from "@/lib/api/map";
import { buildLadder } from "./ladder";
import { cellTone, toneColor, type ColorMode, type HeatIndex } from "./tone";

/** sm → sahna birligi (metr). */
const CM = 1 / 100;

/** Stellajlar orasidagi ustun eni, sm. */
const UPRIGHT_CM = 10;

/**
 * Qatorning uch o'lchamli ko'rinishi.
 *
 * Joylashuv manzilning O'ZIDAN quriladi (TZ §5.1):
 *   X — stellaj va undagi yacheyka (qator bo'ylab yurish yo'nalishi)
 *   Y — qavat (yuqoriga)
 *   Z — katak chuqurligi
 *
 * Katak o'lchami reyestrdan olinadi; to'ldirilmagan bo'lsa katak
 * TURIDAN nominal qiymat ishlatiladi — nol o'lchamli katak
 * chizmada ko'rinmas bo'lib, qator «teshik» bo'lib qolardi.
 *
 * Rang 2D chizma bilan BIR XIL va bitta joydan keladi
 * (`tone.ts`): ikki ko'rinish bir xil katakni boshqacha rang
 * bilan ko'rsatsa, xodim qaysi biriga ishonishni bilmasdi. Rang
 * nimani bildirishi — holat, ABC yoki harakat — chizmada
 * tanlanadi va 3D unga ergashadi.
 */

interface Hover {
  x: number;
  y: number;
  code: string;
  tone: string;
  qty: string;
  skus: number;
  size: string;
  measured: boolean;
}

export function AisleScene({
  cells,
  selected,
  onSelect,
  mode,
  heat,
}: {
  cells: MapCell[];
  selected: MapCell | null;
  onSelect: (cell: MapCell) => void;
  mode: ColorMode;
  heat?: { index: HeatIndex; scale: HeatScale };
}) {
  const host = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected?.location_id ?? null;

  useEffect(() => {
    const element = host.current;
    if (!element || cells.length === 0) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.05, 400);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    element.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x8a8f99, 1.7));
    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(6, 12, 8);
    scene.add(sun);

    // --- Geometriya: stellaj kengligi va qavat balandligi ---
    //
    // Har bir stellajning eni — undagi eng ko'p yacheykali
    // qavatning yig'indisi; qavat balandligi esa o'sha qavatdagi
    // eng baland katak. Shunday qilinmasa turli o'lchamli
    // kataklar bir-birining ustiga chiqib ketardi.
    const placed = cells
      .map((cell) => ({ cell, address: parseAddress(cell.code) }))
      .filter((item): item is { cell: MapCell; address: NonNullable<ReturnType<typeof parseAddress>> } =>
        item.address !== null,
      );

    const rackWidth = new Map<number, number>();
    const rackCellWidth = new Map<number, Map<number, number>>();
    for (const { cell, address } of placed) {
      const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
      const perLevel = rackCellWidth.get(address.rack) ?? new Map<number, number>();
      perLevel.set(address.level, (perLevel.get(address.level) ?? 0) + size.length);
      rackCellWidth.set(address.rack, perLevel);
    }
    for (const [rack, perLevel] of rackCellWidth) {
      rackWidth.set(rack, Math.max(...perLevel.values()));
    }

    const racks = [...rackWidth.keys()].sort((a, b) => a - b);

    const rackOffset = new Map<number, number>();
    let cursor = 0;
    for (const rack of racks) {
      rackOffset.set(rack, cursor);
      cursor += (rackWidth.get(rack) ?? 100) + UPRIGHT_CM;
    }
    const totalWidth = cursor;

    // Qavat balandligi 2D chizma bilan BITTA narvondan
    // (`ladder.ts`): stellajda 1-4 qavat bo'lmasa, 5-qavat
    // o'rnida qoladi, polga tushmaydi.
    const ladder = buildLadder(
      placed.map(({ cell, address }) => ({
        rack: address.rack,
        level: address.level,
        heightCm: cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm).height,
      })),
    );
    const height = ladder.totalCm;

    // Yacheykalar qavat ichida tartib bilan joylanadi.
    //
    // BITTA `InstancedMesh`: qatorda 13 mingdan ortiq katak
    // bo'lishi mumkin va har biri alohida mesh bo'lsa sahna
    // sekinlashardi. O'lcham har bir nusxaning matritsasida —
    // shuning uchun geometriya birlik kub.
    const cursorByRackLevel = new Map<string, number>();
    const order: MapCell[] = [];
    const unit = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshLambertMaterial();
    const boxes = new THREE.InstancedMesh(unit, material, placed.length);
    const matrix = new THREE.Matrix4();
    const color = new THREE.Color();

    placed.forEach(({ cell, address }, index) => {
      const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
      const key = `${address.rack}:${address.level}`;
      const used = cursorByRackLevel.get(key) ?? 0;
      cursorByRackLevel.set(key, used + size.length);

      // Balandlik narvondan chegaralanadi: butun bo'yli pallet
      // ustidagi qavatga kirib ketmasligi kerak.
      const levelSpan = ladder.spanOf(address.rack, address.level) || size.height;
      matrix.compose(
        new THREE.Vector3(
          ((rackOffset.get(address.rack) ?? 0) + used + size.length / 2 - totalWidth / 2) * CM,
          (ladder.bottomOf(address.level) + levelSpan / 2) * CM,
          0,
        ),
        new THREE.Quaternion(),
        // 2 sm tirqish: qo'shni kataklar chegarasi ko'rinsin.
        new THREE.Vector3(
          Math.max(size.length - 2, 1) * CM,
          Math.max(levelSpan - 2, 1) * CM,
          size.width * CM,
        ),
      );
      boxes.setMatrixAt(index, matrix);
      boxes.setColorAt(index, color.setHex(toneColor(cellTone(cell, mode, heat).key)));
      order.push(cell);
    });
    boxes.instanceMatrix.needsUpdate = true;
    if (boxes.instanceColor) boxes.instanceColor.needsUpdate = true;
    scene.add(boxes);

    // Pol: qator qayerda turganini ko'rsatadi.
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(totalWidth * CM * 1.05, 6),
      new THREE.MeshLambertMaterial({ color: 0xeef1f5, side: THREE.DoubleSide }),
    );
    floor.rotation.x = -Math.PI / 2;
    scene.add(floor);

    // Kadrlash: qator o'nlab metr uzunlikda bo'lishi mumkin
    // (reyestrda bitta qatorda 50 dan ortiq stellaj bor). Butun
    // uzunlikni ekranga sig'dirish kataklarni ko'rinmas chiziqqa
    // aylantirardi — shuning uchun kamera qator BOSHIDA turadi,
    // xuddi xodim koridorga kirgandek. Qolganiga surib boriladi.
    const WINDOW_CM = 2500;
    const windowWidth = Math.min(totalWidth, WINDOW_CM);
    const focusX = (-totalWidth / 2 + windowWidth / 2) * CM;
    const span = Math.max(windowWidth * CM, height * CM, 3);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(focusX, (height * CM) / 2, 0);
    camera.position.set(focusX - span * 0.35, height * CM + span * 0.35, span * 0.95);
    // Butun qatordan uzoqlashib ketmasin: bo'sh ekran qoladi.
    controls.maxDistance = Math.max(totalWidth * CM, span * 3);
    controls.enableDamping = true;
    controls.update();

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const pick = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObject(boxes, false)[0];
      return { hit, rect };
    };

    const onMove = (event: PointerEvent) => {
      const { hit, rect } = pick(event);
      if (!hit) {
        setHover(null);
        return;
      }
      const cell = order[hit.instanceId ?? -1];
      if (!cell) {
        setHover(null);
        return;
      }
      const size = cellSize(cell.cell_type, cell.length_cm, cell.width_cm, cell.height_cm);
      setHover({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        code: cell.code,
        tone: cellTone(cell, mode, heat).label,
        qty: cell.on_hand_qty,
        skus: cell.distinct_sku_count,
        size: `${size.length}×${size.width}×${size.height} sm`,
        measured: size.measured,
      });
    };

    const onClick = (event: PointerEvent) => {
      const { hit } = pick(event);
      const cell = hit ? order[hit.instanceId ?? -1] : undefined;
      if (cell) onSelect(cell);
    };

    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("pointerleave", () => setHover(null));
    renderer.domElement.addEventListener("click", onClick);

    const resize = () => {
      const width = element.clientWidth || 1;
      const viewHeight = element.clientHeight || 1;
      renderer.setSize(width, viewHeight, false);
      camera.aspect = width / viewHeight;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);

    // Tanlangan katak ajralib tursin — 2D chizmada ham shunday.
    // Rang faqat TANLOV o'zgarganda qayta yoziladi: har kadrda
    // 13 mingta nusxani aylanib chiqish bekorga ish bo'lardi.
    let painted: string | null = null;
    const paintSelection = () => {
      if (painted === selectedRef.current) return;
      const previous = painted;
      painted = selectedRef.current;
      order.forEach((cell, index) => {
        if (cell.location_id !== previous && cell.location_id !== painted) return;
        const active = cell.location_id === painted;
        boxes.setColorAt(index,
          color.setHex(active ? 0x111111 : toneColor(cellTone(cell, mode, heat).key)));
      });
      if (boxes.instanceColor) boxes.instanceColor.needsUpdate = true;
    };

    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      paintSelection();
      controls.update();
      renderer.render(scene, camera);
    };
    tick();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("click", onClick);
      controls.dispose();
      renderer.dispose();
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          (object.material as THREE.Material).dispose();
        }
      });
      element.removeChild(renderer.domElement);
    };
  }, [cells, onSelect, mode, heat]);

  return (
    <div className="aisle-scene" ref={host}>
      {hover && (
        <div className="aisle-tip" style={{ left: hover.x + 14, top: hover.y + 14 }}>
          <b>{hover.code}</b>
          <span>{hover.tone}</span>
          <span>Qoldiq: {hover.qty} · {hover.skus} SKU</span>
          <span>
            {hover.size}
            {hover.measured ? "" : " (katak turidan — reyestrda o'lcham yo'q)"}
          </span>
        </div>
      )}
    </div>
  );
}
